import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { Product } from '../../types/bom';
import { listProducts, listRevisions } from '../../services/bomService';
import BOMImportDialog from './BOMImportDialog';

const AssembledModuleList: React.FC = () => {
  const { t } = useTranslation('bom');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // BOM Import dialog state
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [bomProducts, setBomProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [importRevisions, setImportRevisions] = useState<{ revision: string; status: string }[]>([]);

  const loadBomProducts = async () => {
    setLoadingProducts(true);
    try {
      const products = await listProducts();
      setBomProducts(products || []);
    } catch (err) {
      logger.error('Failed to load BOM products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleOpenProductSelector = () => {
    loadBomProducts();
    setShowProductSelector(true);
  };

  const handleSelectProduct = async (product: Product) => {
    try {
      const revisions = await listRevisions(product.product_id);
      setSelectedProduct(product);
      setImportRevisions(
        revisions.map((r: { revision: string; status: string }) => ({ revision: r.revision, status: r.status }))
      );
      setShowProductSelector(false);
      setShowImportDialog(true);
    } catch (err) {
      logger.error('Failed to load revisions:', err);
      setSelectedProduct(product);
      setImportRevisions([]);
      setShowProductSelector(false);
      setShowImportDialog(true);
    }
  };

  const handleImportComplete = () => {
    setShowImportDialog(false);
    setSelectedProduct(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('assembledModules.title', 'Assembled Modules')}
        </h1>
        <div className="flex gap-2">
          <button
            onClick={handleOpenProductSelector}
            className="px-4 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors"
          >
            {t('bom.importBOM', 'Import BOM')}
          </button>
          <button
            onClick={() => navigate('/app/bom/assembled-modules/new')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('assembledModules.newModule', 'New Assembled Module')}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder={t('assembledModules.searchPlaceholder', 'Search by code or name...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Empty State */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('assembledModules.noModules', 'No assembled modules found')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('assembledModules.emptyStateDescription', 'Assembled modules combine PCB modules with mechanical parts. Create your first assembled module to get started.')}
          </p>
          <button
            onClick={() => navigate('/app/bom/assembled-modules/new')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('assembledModules.newModule', 'New Assembled Module')}
          </button>
        </div>
      </div>

      {/* PCB Module Selector Dialog for BOM Import */}
      {showProductSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('bom.selectPCBModuleToImport', 'Select PCB Module to Import BOM')}
              </h2>
              <button
                onClick={() => setShowProductSelector(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b dark:border-gray-700">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t('bom.importToPCBModuleNote', 'BOM files are imported into PCB Modules. Select the target PCB module below.')}
              </p>
            </div>
            <div className="p-6 overflow-auto max-h-[50vh]">
              {loadingProducts ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : bomProducts.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <p>{t('bom.noPCBModulesFound', 'No PCB modules found.')}</p>
                  <button
                    onClick={() => {
                      setShowProductSelector(false);
                      navigate('/app/bom/pcb-modules/new');
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {t('pcbModules.newModule', 'Create PCB Module')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {bomProducts.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => handleSelectProduct(product)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">
                        {product.product_code}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {product.product_name} • Rev {product.current_revision}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BOM Import Dialog */}
      {selectedProduct && (
        <BOMImportDialog
          isOpen={showImportDialog}
          onClose={() => {
            setShowImportDialog(false);
            setSelectedProduct(null);
          }}
          productId={selectedProduct.product_id}
          currentRevision={selectedProduct.current_revision}
          revisions={importRevisions}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
};

export default AssembledModuleList;
